/**
 * @Description 商圈信息（目前的逻辑是商圈规划组件内不能修改地址，地址自动从详细地址组件中带出，不可修改。之后用详细地址，获取商圈信息）
 *  控件 提交、回显的参数格式
 *
 * 按实际数据更新注释内容
 * 旧版注释
 {
      isInCluster: true,
      clusterId: 1,
      clusterName: '钱江新城商圈',
      clusterTypeName: '一级商圈',
      clusterScore: '95分',
      distance: 3333,
      openStores: 0,
      planStores: 1,
      lat: 30,
      lng: 120,
  }

新版注释
不在开店区域内时
{
    "isInCluster": false,
    "lat": 30.288628,
    "lng": 120.15107,
    "modelConfigId": 13,
    "clusterScore": 76.4, // 填充的是点位测评评分
    "areaName": "朝晖小区",
    "cityId": 87,
    "cityName": "杭州市",
}
在开店区域内时
{
    "isInCluster": true,
    "lat": 30.277416,
    "lng": 120.155855,
    "modelConfigId": 13,
    "clusterScore": "55.4",
    "clusterId": 7246268,
    "planClusterId": null,  // 没用
    "clusterName": "苏宁生活广场",
    "clusterTypeName": "商业区/商业体",
    "planStores": null, // 没用
    "openStores": 0, // 没用
    "distance": 31.92473649163244, // 没用
    "planSpots": null // 区域内集客点对象数组
    "planSpotId": null // 手动填充，选中的集客点id
    "planSpotName": null // 手动填充，选中的集客点name
}
 */
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import V2FormInput from '@/common/components/Form/V2FormInput/V2FormInput';
import { Row, Col } from 'antd';
import { isNotEmpty, isNotEmptyAny } from '@lhb/func';
import { parseValueCatch } from '../config';
// import IconFont from '@/common/components/IconFont';
// import AreaDetailDrawer from '@/views/iterate/pages/siteselectionmap/components/AreaDetailDrawer';
// import BusinessOverview from '../../StoreDetail/components/DynamicDetail/components/BusinessOverview';
import AreaOverview from '../../StoreDetail/components/DynamicDetail/components/AreaOverview';
import SpotEvaluationOverview from '../../StoreDetail/components/DynamicDetail/components/SpotEvaluationOverview';
import { globalOpeningAreaAlias } from '@/common/utils/ways';
import UserInfoContext from '@/layout/context';

/**
 * @Description 商圈信息组件
 */
const BusinessInfo: FC<{
  form: any;
  formItemData: any;
  disabled: boolean;
  /** 商圈信息数据：地址变动时外部传入的商圈数据 */
  businessInfoValue: any;
  /** 益禾堂选择集客点后带出集客点的商圈类型 */
  setCustomGatheringCategoryForYHT: any;
  /** 禁用匹配商圈点击查看详情 */
  disabledArea: boolean;
}> = ({
  form,
  formItemData,
  disabled,
  businessInfoValue, // 地址变动时外部传入的商圈数据
  setCustomGatheringCategoryForYHT, // 益禾堂选择集客点后带出集客点的商圈类型
  disabledArea, // 禁用匹配商圈点击查看详情
}) => {
  const identification = formItemData.identification; // 字段

  const [curCompValue, setCurCompValue] = useState<any>(null); // 自定义组件的值

  const { tenantInfo: { globalAliasItemVOs } } = useContext(UserInfoContext); // 租户信息

  const openingAreaTaskAlias = useMemo(() => {
    return globalOpeningAreaAlias(globalAliasItemVOs);
  }, [globalAliasItemVOs]);

  /**
   * @description 初次渲染formItem信息
   */
  useEffect(() => {
    const curVal = parseValueCatch(formItemData);
    let planSpotId = curVal?.planSpotId;
    if (curVal?.savePlanSpot && !curVal.planSpotId) {
      // 机会点表单变动，有快捷创建的集客点信息切无planSpotId则取savePlanSpot
      planSpotId = curVal.savePlanSpot?.planSpots?.[0].name;
    }
    form.setFieldsValue({
      [`${identification}-clusterName`]: curVal?.clusterName,
      [`${identification}-clusterTypeName`]: curVal?.clusterTypeName,
      [`${identification}-clusterScore`]: curVal?.clusterScore,
      [`${identification}-planSpotId`]: planSpotId,
    });
    setCurCompValue(curVal);

  }, [formItemData.textValue]);

  const modelId = useMemo(() => {
    if (!formItemData) {
      return null;
    }
    const restriction = JSON.parse(formItemData?.restriction);
    return restriction?.modelId;
  }, [formItemData]);

  /**
   * @description 地址变动时重新设置组件内容
   */
  useEffect(() => {
    // 初次渲染时，传入的空信息，会把编辑用详情更新掉，不予处理
    if (!isNotEmptyAny(businessInfoValue)) return;
    let planSpotId = businessInfoValue?.planSpotId;
    if (String(businessInfoValue?.savePlanSpot?.modelClusterId) === String(businessInfoValue?.clusterId) && !businessInfoValue.planSpotId) {
      // 如果有planSpotId则使用planSpotId，快捷创建的集客点planSpotId会传 null
      planSpotId = isNotEmpty(formItemData.textValue) ? JSON.parse(formItemData.textValue)?.savePlanSpot?.planSpots?.[0].name : null;
      // 之前创建过集客点，有切换到其他开店区域又切换回来创建过集客点的开店区域
      if (!businessInfoValue?.isInit && businessInfoValue?.savePlanSpot?.firstLevelCategory) { // 移动地图触发
        setCustomGatheringCategoryForYHT && (setCustomGatheringCategoryForYHT({
          firstLevelCategory: businessInfoValue?.savePlanSpot.firstLevelCategory
        }));
      }
    }
    // 编辑时，如果是初始化不执行set，但是如果是移动地图就要重置
    !businessInfoValue?.isInit && setCustomGatheringCategoryForYHT && (setCustomGatheringCategoryForYHT({ isReset: true }));
    form.setFieldsValue({
      [`${identification}-clusterName`]: businessInfoValue?.clusterName,
      [`${identification}-clusterTypeName`]: businessInfoValue?.clusterTypeName,
      [`${identification}-clusterScore`]: businessInfoValue?.clusterScore,
      [`${identification}-planSpotId`]: planSpotId,
    });
    setCurCompValue(businessInfoValue);
  }, [businessInfoValue]);

  const renderNoAddress = () => {
    return (
      <Row gutter={24}>
        <Col span={12}>
          <V2FormInput
            label={openingAreaTaskAlias}
            placeholder='选择详细地址后自动获取'
            disabled
          />
        </Col>
      </Row>
    );
  };

  if (!curCompValue) {
    // 还未选地址
    return renderNoAddress();
  } else if (!curCompValue.isInCluster) {
    // 不处于任何开店区域内，显示周边点位测评信息
    return (
      <SpotEvaluationOverview
        lng={businessInfoValue?.lng}
        lat={businessInfoValue?.lat}
        modelId={modelId}
        isDetailPage={false}
      />
    );
  } else {
    // 处于开店区域内，显示区域信息
    return (
      <AreaOverview
        modelId={modelId}
        clusterId={curCompValue.clusterId}
        isDetailPage={false}
        form={form}
        formItemData={formItemData}
        curCompValue={curCompValue}
        identification={identification}
        disabled={disabled}
        setCustomGatheringCategoryForYHT={setCustomGatheringCategoryForYHT}
        disabledArea={disabledArea}
      />
    );
  }
};

export default BusinessInfo;
